import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

// Import textures directly so webpack bundles them (avoids nginx 404 on /textures/)
import earthDayUrl from './earth_atmos_2048.jpg';
import earthSpecularUrl from './earth_specular_2048.jpg';
import earthNormalUrl from './earth_normal_2048.jpg';
import earthNightUrl from './earth-night.jpg';
import earthCloudsUrl from './earth_clouds_1024.png';

function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return gl instanceof WebGLRenderingContext;
  } catch {
    return false;
  }
}

/**
 * CSS fallback when WebGL is unavailable
 */
function EarthFallback() {
  return (
    <div className='relative w-full h-full flex items-center justify-center'>
      <div
        className='rounded-full'
        style={{
          width: 'min(420px, 50vh)',
          height: 'min(420px, 50vh)',
          background:
            'radial-gradient(circle at 35% 35%, #1a6fb5 0%, #0c3d6e 40%, #051c34 70%, #020a14 100%)',
          boxShadow: '0 0 80px rgba(14, 165, 233, 0.3), inset -30px -20px 60px rgba(0,0,0,0.6)',
          animation: 'earthPulse 4s ease-in-out infinite',
        }}
      />
      <div
        className='absolute rounded-full'
        style={{
          width: 'min(460px, 55vh)',
          height: 'min(460px, 55vh)',
          border: '2px solid rgba(100, 180, 255, 0.15)',
          boxShadow: '0 0 40px rgba(100, 180, 255, 0.1), inset 0 0 40px rgba(100, 180, 255, 0.05)',
        }}
      />
      <style>{`
        @keyframes earthPulse {
          0%, 100% { box-shadow: 0 0 80px rgba(14, 165, 233, 0.3), inset -30px -20px 60px rgba(0,0,0,0.6); }
          50% { box-shadow: 0 0 100px rgba(14, 165, 233, 0.4), inset -30px -20px 60px rgba(0,0,0,0.6); }
        }
      `}</style>
    </div>
  );
}

export default function EarthGlobe() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [webglFailed, setWebglFailed] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !isWebGLAvailable()) {
      setWebglFailed(true);
      return;
    }

    const container = containerRef.current;
    let animationFrameId: number;

    try {
      // --- Scene Setup ---
      const scene = new THREE.Scene();
      const width = container.clientWidth;
      const height = container.clientHeight;

      const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
      camera.position.z = 4.5;

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      // ACES Filmic tone mapping for cinematic look
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.2;
      container.appendChild(renderer.domElement);

      // --- Post-Processing (Bloom) ---
      const renderPass = new RenderPass(scene, camera);
      const bloomPass = new UnrealBloomPass(new THREE.Vector2(width, height), 1.5, 0.4, 0.85);
      bloomPass.threshold = 0.2;
      bloomPass.strength = 0.35;
      bloomPass.radius = 0.5;

      const composer = new EffectComposer(renderer);
      composer.addPass(renderPass);
      composer.addPass(bloomPass);

      // --- Texture Loader with color space ---
      const textureLoader = new THREE.TextureLoader();
      const loadTex = (url: string, srgb = true) => {
        const tex = textureLoader.load(url, t => {
          if (srgb) t.colorSpace = THREE.SRGBColorSpace;
          t.needsUpdate = true;
          earthMaterial.needsUpdate = true;
        });
        if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
        return tex;
      };

      // --- Earth ---
      const earthGeometry = new THREE.SphereGeometry(1, 64, 64);
      const dayMap = loadTex(earthDayUrl);
      const specMap = loadTex(earthSpecularUrl, false);
      const normMap = loadTex(earthNormalUrl, false);
      const nightMap = loadTex(earthNightUrl);
      const earthMaterial = new THREE.MeshPhongMaterial({
        map: dayMap,
        specularMap: specMap,
        normalMap: normMap,
        specular: new THREE.Color(0x333333),
        shininess: 25,
        emissiveMap: nightMap,
        emissive: new THREE.Color(0xffffaa),
        emissiveIntensity: 0.3,
      });
      const earth = new THREE.Mesh(earthGeometry, earthMaterial);
      earth.rotation.z = (23.5 * Math.PI) / 180;
      scene.add(earth);

      // --- Cloud Layer ---
      const cloudGeometry = new THREE.SphereGeometry(1.015, 64, 64);
      const cloudTex = textureLoader.load(earthCloudsUrl, t => {
        t.colorSpace = THREE.SRGBColorSpace;
        t.needsUpdate = true;
      });
      cloudTex.colorSpace = THREE.SRGBColorSpace;
      const cloudMaterial = new THREE.MeshLambertMaterial({
        map: cloudTex,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const clouds = new THREE.Mesh(cloudGeometry, cloudMaterial);
      earth.add(clouds);

      // --- Atmosphere Glow (Shader from reference) ---
      const atmosGeometry = new THREE.SphereGeometry(1.18, 64, 64);
      const atmosMaterial = new THREE.ShaderMaterial({
        vertexShader: `
          varying vec3 vNormal;
          void main() {
            vNormal = normalize(normalMatrix * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          varying vec3 vNormal;
          void main() {
            float intensity = pow(0.6 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.5);
            gl_FragColor = vec4(0.0, 0.5, 1.0, 1.0) * intensity * 1.0;
          }
        `,
        blending: THREE.AdditiveBlending,
        side: THREE.BackSide,
        transparent: true,
        depthWrite: false,
      });
      const atmosphere = new THREE.Mesh(atmosGeometry, atmosMaterial);
      scene.add(atmosphere);

      // --- Lighting (high contrast, reference style) ---
      const ambientLight = new THREE.AmbientLight(0x050510);
      scene.add(ambientLight);

      const sunLight = new THREE.DirectionalLight(0xffffff, 2.5);
      sunLight.position.set(5, 3, 5);
      scene.add(sunLight);

      const rimLight = new THREE.DirectionalLight(0x3366ff, 0.6);
      rimLight.position.set(-5, -3, -5);
      scene.add(rimLight);

      // --- Stars Background (4000 with vertex colors) ---
      const starsCount = 4000;
      const starsPositions = new Float32Array(starsCount * 3);
      const starsColors = new Float32Array(starsCount * 3);
      for (let i = 0; i < starsCount * 3; i += 3) {
        starsPositions[i] = (Math.random() - 0.5) * 80;
        starsPositions[i + 1] = (Math.random() - 0.5) * 80;
        starsPositions[i + 2] = (Math.random() - 0.5) * 80;
        starsColors[i] = 0.8 + Math.random() * 0.2;
        starsColors[i + 1] = 0.8 + Math.random() * 0.2;
        starsColors[i + 2] = 1.0;
      }
      const starsGeometry = new THREE.BufferGeometry();
      starsGeometry.setAttribute('position', new THREE.BufferAttribute(starsPositions, 3));
      starsGeometry.setAttribute('color', new THREE.BufferAttribute(starsColors, 3));
      const starsMaterial = new THREE.PointsMaterial({
        size: 0.05,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        sizeAttenuation: true,
      });
      const stars = new THREE.Points(starsGeometry, starsMaterial);
      scene.add(stars);

      // --- Mouse Interaction (subtle camera follow) ---
      let mouseX = 0;
      let mouseY = 0;
      const handleMouseMove = (e: MouseEvent) => {
        const rect = container.getBoundingClientRect();
        mouseX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mouseY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      };
      container.addEventListener('mousemove', handleMouseMove);

      // --- Animation Loop ---
      const animate = () => {
        // Earth and clouds rotate at slightly different speeds
        earth.rotation.y += 0.0008;
        clouds.rotation.y += 0.0003; // relative to earth (additive)

        // Subtle camera follow mouse
        camera.position.x += (mouseX * 0.3 - camera.position.x) * 0.02;
        camera.position.y += (mouseY * 0.2 - camera.position.y) * 0.02;
        camera.lookAt(0, 0, 0);

        // Render with post-processing
        composer.render();
        animationFrameId = requestAnimationFrame(animate);
      };

      animate();

      // --- Resize Handler ---
      const handleResize = () => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
        composer.setSize(w, h);
      };
      window.addEventListener('resize', handleResize);

      // --- Cleanup ---
      return () => {
        window.removeEventListener('resize', handleResize);
        container.removeEventListener('mousemove', handleMouseMove);
        cancelAnimationFrame(animationFrameId);

        earthGeometry.dispose();
        earthMaterial.dispose();
        cloudGeometry.dispose();
        cloudMaterial.dispose();
        atmosGeometry.dispose();
        atmosMaterial.dispose();
        starsGeometry.dispose();
        starsMaterial.dispose();

        composer.dispose();
        renderer.dispose();
        if (container && renderer.domElement) {
          container.removeChild(renderer.domElement);
        }
      };
    } catch {
      setWebglFailed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (webglFailed) {
    return <EarthFallback />;
  }

  return <div ref={containerRef} className='w-full h-full' />;
}
